"use client";

import React, { type CSSProperties, useMemo, useState } from "react";
import {
  ArrowRight,
  ChevronRight,
  Cog,
  Layers,
  ListTree,
  Minus,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";
import {
  ArrowRightIcon as HeroArrowRightIcon,
  ChevronRightIcon as HeroChevronRightIcon,
  Cog6ToothIcon as HeroCogIcon,
  ListBulletIcon as HeroListBulletIcon,
  MagnifyingGlassIcon as HeroSearchIcon,
  MinusIcon as HeroMinusIcon,
  PlusIcon as HeroPlusIcon,
  Squares2X2Icon as HeroSquaresIcon,
  WrenchScrewdriverIcon as HeroWrenchScrewdriverIcon,
  XMarkIcon as HeroXMarkIcon,
} from "@heroicons/react/24/outline";
import type { CategoryDetails, SubCategory } from "../../../types";
import {
  serviceCategorySidebarNavigatorTokens,
  type ServiceCategorySidebarNavigatorColorToken,
  type ServiceCategorySidebarNavigatorIconToken,
} from "./ServiceCategorySidebarNavigator.tokens";

type CssVarStyle = CSSProperties & Record<`--${string}`, string | number | undefined>;
type IconComponent = React.ElementType;
type NavigatorEntityKind = "category" | "service";
type NavigatorId = string | number;

const t = serviceCategorySidebarNavigatorTokens;

const iconRegistry: Record<ServiceCategorySidebarNavigatorIconToken, IconComponent> = {
  arrowRight: ArrowRight,
  chevronRight: ChevronRight,
  search: Search,
  plus: Plus,
  minus: Minus,
  x: X,
  settings: Cog,
  wrench: Wrench,
  layers: Layers,
  list: ListTree,
  sparkles: Sparkles,
  shieldCheck: ShieldCheck,
  heroArrowRight: HeroArrowRightIcon,
  heroChevronRight: HeroChevronRightIcon,
  heroSearch: HeroSearchIcon,
  heroPlus: HeroPlusIcon,
  heroMinus: HeroMinusIcon,
  heroXMark: HeroXMarkIcon,
  heroCog: HeroCogIcon,
  heroWrenchScrewdriver: HeroWrenchScrewdriverIcon,
  heroSquares: HeroSquaresIcon,
  heroListBullet: HeroListBulletIcon,
};

export type ServiceCategorySidebarNavigatorLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  style?: CSSProperties;
  target?: string;
  rel?: string;
  onClick?: React.MouseEventHandler;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type ServiceCategorySidebarNavigatorCta = {
  label: React.ReactNode;
  href: string;
  ariaLabel?: string;
  target?: string;
  rel?: string;
  icon?: React.ReactNode;
  iconToken?: ServiceCategorySidebarNavigatorIconToken;
};

type ServiceCategorySidebarNavigatorSubCategoryFields = Omit<
  Partial<SubCategory>,
  | "id"
  | "title"
  | "name"
  | "description"
  | "link"
  | "serviceCategories"
  | "accentColor"
  | "accentDarkColor"
>;

export type ServiceCategorySidebarNavigatorService =
  ServiceCategorySidebarNavigatorSubCategoryFields & {
    id: NavigatorId;
    title?: string;
    name?: string;
    label?: string;
    text?: string;
    description?: string;
    href?: string;
    link?: string;
    target?: string;
    rel?: string;
    ariaLabel?: string;
    cta?: Partial<ServiceCategorySidebarNavigatorCta>;
    ctaLabel?: React.ReactNode;
    ctaHref?: string;
    ctaAriaLabel?: string;
    icon?: React.ReactNode;
    iconToken?: ServiceCategorySidebarNavigatorIconToken;
    arrowIcon?: React.ReactNode;
    arrowIconToken?: ServiceCategorySidebarNavigatorIconToken;
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
  };

export type ServiceCategorySidebarNavigatorCategory =
  ServiceCategorySidebarNavigatorSubCategoryFields & {
    id: NavigatorId;
    title?: string;
    name?: string;
    label?: string;
    text?: string;
    description?: string;
    href?: string;
    link?: string;
    target?: string;
    rel?: string;
    ariaLabel?: string;
    services?: ServiceCategorySidebarNavigatorService[];
    items?: ServiceCategorySidebarNavigatorService[];
    serviceCategories?: ServiceCategorySidebarNavigatorService[];
    cta?: Partial<ServiceCategorySidebarNavigatorCta>;
    ctaLabel?: React.ReactNode;
    ctaHref?: string;
    ctaAriaLabel?: string;
    icon?: React.ReactNode;
    iconToken?: ServiceCategorySidebarNavigatorIconToken;
    arrowIcon?: React.ReactNode;
    arrowIconToken?: ServiceCategorySidebarNavigatorIconToken;
    expandIcon?: React.ReactNode;
    expandIconToken?: ServiceCategorySidebarNavigatorIconToken;
    collapseIcon?: React.ReactNode;
    collapseIconToken?: ServiceCategorySidebarNavigatorIconToken;
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
  };

export type ServiceCategorySidebarNavigatorColors = Partial<
  Record<ServiceCategorySidebarNavigatorColorToken, string>
>;

export type ServiceCategorySidebarNavigatorProps = {
  service?: CategoryDetails;
  serviceCategory?: CategoryDetails;
  categories?: ServiceCategorySidebarNavigatorCategory[];
  groups?: ServiceCategorySidebarNavigatorCategory[];
  services?: ServiceCategorySidebarNavigatorService[];
  items?: ServiceCategorySidebarNavigatorService[];
  Link?: ServiceCategorySidebarNavigatorLinkComponent;
  title?: React.ReactNode;
  titleComponent?: "h2" | "h3" | "h4" | "h5" | "h6";
  description?: React.ReactNode;
  text?: React.ReactNode;
  navTitle?: string;
  cta?: ServiceCategorySidebarNavigatorCta;
  ctaLabel?: React.ReactNode;
  ctaHref?: string;
  ctaAriaLabel?: string;
  supportText?: React.ReactNode;
  supportLink?: ServiceCategorySidebarNavigatorCta;
  supportLinkLabel?: React.ReactNode;
  supportLinkHref?: string;
  supportLinkAriaLabel?: string;
  supportTextSuffix?: React.ReactNode;
  itemCtaLabel?: string;
  categoryCtaLabel?: string;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  searchValue?: string;
  defaultSearchValue?: string;
  onSearchChange?: (value: string) => void;
  showSearch?: boolean;
  showCategoryIcons?: boolean;
  showServiceIcons?: boolean;
  showCategoryDescriptions?: boolean;
  showServiceDescriptions?: boolean;
  defaultOpen?: boolean;
  initiallyOpen?: boolean;
  defaultOpenCategories?: boolean | NavigatorId[];
  openCategories?: boolean | NavigatorId[];
  allowMultipleOpen?: boolean;
  onOpenCategoriesChange?: (categoryIds: NavigatorId[]) => void;
  onCategoryClick?: (
    category: ServiceCategorySidebarNavigatorCategory,
    event: React.MouseEvent
  ) => void;
  onServiceClick?: (
    service: ServiceCategorySidebarNavigatorService,
    category: ServiceCategorySidebarNavigatorCategory,
    event: React.MouseEvent
  ) => void;
  categoryIconToken?: ServiceCategorySidebarNavigatorIconToken;
  serviceIconToken?: ServiceCategorySidebarNavigatorIconToken;
  categoryArrowIconToken?: ServiceCategorySidebarNavigatorIconToken;
  serviceArrowIconToken?: ServiceCategorySidebarNavigatorIconToken;
  expandIconToken?: ServiceCategorySidebarNavigatorIconToken;
  collapseIconToken?: ServiceCategorySidebarNavigatorIconToken;
  colors?: ServiceCategorySidebarNavigatorColors;
  colours?: ServiceCategorySidebarNavigatorColors;
  accentColor?: string;
  accentColour?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  serviceSurfaceColor?: string;
  textColor?: string;
  borderColor?: string;
  maxWidth?: number | string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  emptyText?: React.ReactNode;
};

type ResolvedService = {
  key: string;
  id: NavigatorId;
  source: ServiceCategorySidebarNavigatorService;
  title: string;
  description?: string;
  href: string;
  target?: string;
  rel?: string;
  ariaLabel?: string;
  accentColor: string;
  icon?: React.ReactNode;
  iconToken?: ServiceCategorySidebarNavigatorIconToken;
  arrowIcon?: React.ReactNode;
  arrowIconToken?: ServiceCategorySidebarNavigatorIconToken;
  searchText: string;
};

type ResolvedCategory = {
  key: string;
  id: NavigatorId;
  source: ServiceCategorySidebarNavigatorCategory;
  title: string;
  description?: string;
  href?: string;
  target?: string;
  rel?: string;
  ariaLabel?: string;
  accentColor: string;
  icon?: React.ReactNode;
  iconToken?: ServiceCategorySidebarNavigatorIconToken;
  arrowIcon?: React.ReactNode;
  arrowIconToken?: ServiceCategorySidebarNavigatorIconToken;
  expandIcon?: React.ReactNode;
  expandIconToken?: ServiceCategorySidebarNavigatorIconToken;
  collapseIcon?: React.ReactNode;
  collapseIconToken?: ServiceCategorySidebarNavigatorIconToken;
  services: ResolvedService[];
  searchText: string;
};

const DefaultLink: ServiceCategorySidebarNavigatorLinkComponent = ({
  href,
  children,
  ...props
}) => (
  <a href={href} {...props}>
    {children}
  </a>
);

const serviceCategorySidebarNavigatorCss = `
.service-category-sidebar-navigator {
  --scsn-bg: ${t.colors.background};
  --scsn-surface: ${t.colors.surface};
  --scsn-service-surface: ${t.colors.serviceSurface};
  --scsn-service-surface-hover: ${t.colors.serviceSurfaceHover};
  --scsn-text: ${t.colors.text};
  --scsn-text-soft: ${t.colors.textSoft};
  --scsn-text-muted: ${t.colors.textMuted};
  --scsn-accent: ${t.colors.accent};
  --scsn-accent-contrast: ${t.colors.accentContrast};
  --scsn-border: ${t.colors.border};
  --scsn-strong-border: ${t.colors.strongBorder};
  --scsn-search-bg: ${t.colors.searchBackground};
  --scsn-icon-bg: ${t.colors.iconBackground};
  --scsn-shadow: ${t.colors.shadow};
  --scsn-hover-shadow: ${t.colors.hoverShadow};
  --scsn-max-width: ${t.sizes.maxWidth}px;
  width: 100%;
  color: var(--scsn-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}

.service-category-sidebar-navigator,
.service-category-sidebar-navigator * {
  box-sizing: border-box;
}

.service-category-sidebar-navigator-panel {
  width: 100%;
  max-width: var(--scsn-max-width);
  margin: 0;
  padding: ${t.spacing.panel}px;
  border: 1px solid var(--scsn-border);
  border-radius: ${t.radius.panel}px;
  background: var(--scsn-bg);
  box-shadow: var(--scsn-shadow);
}

.service-category-sidebar-navigator-search {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 42px;
  margin-bottom: ${t.spacing.sectionGap}px;
  color: var(--scsn-text-muted);
}

.service-category-sidebar-navigator-search-icon {
  position: absolute;
  left: 11px;
  width: 16px;
  height: 16px;
  color: var(--scsn-text-muted);
  pointer-events: none;
}

.service-category-sidebar-navigator-input {
  width: 100%;
  min-width: 0;
  min-height: 42px;
  padding: 10px 38px 10px 35px;
  border: 1px solid var(--scsn-border);
  border-radius: ${t.radius.search}px;
  color: var(--scsn-text);
  background: var(--scsn-search-bg);
  font-size: ${t.typography.search.fontSize}px;
  line-height: ${t.typography.search.lineHeight};
  font-weight: ${t.typography.search.fontWeight};
  letter-spacing: ${t.typography.search.letterSpacing};
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.service-category-sidebar-navigator-input::placeholder {
  color: color-mix(in srgb, var(--scsn-text-muted) 78%, transparent);
}

.service-category-sidebar-navigator-input::-webkit-search-decoration,
.service-category-sidebar-navigator-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

.service-category-sidebar-navigator-input:focus {
  border-color: color-mix(in srgb, var(--scsn-accent) 58%, var(--scsn-border));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--scsn-accent) 14%, transparent);
}

.service-category-sidebar-navigator-clear {
  position: absolute;
  right: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  color: var(--scsn-text-muted);
  background: transparent;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease;
}

.service-category-sidebar-navigator-clear:hover,
.service-category-sidebar-navigator-clear:focus-visible {
  color: var(--scsn-accent);
  background: color-mix(in srgb, var(--scsn-accent) 10%, transparent);
}

.service-category-sidebar-navigator-clear:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--scsn-accent) 18%, transparent);
  outline-offset: 2px;
}

.service-category-sidebar-navigator-clear-icon {
  width: 15px;
  height: 15px;
}

.service-category-sidebar-navigator-header {
  display: grid;
  gap: ${t.spacing.headerGap}px;
  margin-bottom: ${t.spacing.sectionGap}px;
}

.service-category-sidebar-navigator-title {
  margin: 0;
  color: var(--scsn-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  overflow-wrap: anywhere;
}

.service-category-sidebar-navigator-description {
  max-width: 30rem;
  margin: 0;
  color: var(--scsn-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
  overflow-wrap: anywhere;
}

.service-category-sidebar-navigator-cta,
.service-category-sidebar-navigator-support-link {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-width: 0;
  gap: 7px;
  color: var(--scsn-accent);
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-decoration: none;
}

.service-category-sidebar-navigator-cta:hover,
.service-category-sidebar-navigator-cta:focus-visible,
.service-category-sidebar-navigator-support-link:hover,
.service-category-sidebar-navigator-support-link:focus-visible {
  color: var(--scsn-accent);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.service-category-sidebar-navigator-cta:focus-visible,
.service-category-sidebar-navigator-support-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--scsn-accent) 18%, transparent);
  outline-offset: 4px;
  border-radius: ${t.radius.focus}px;
}

.service-category-sidebar-navigator-cta-icon,
.service-category-sidebar-navigator-support-icon {
  width: ${t.sizes.ctaIcon}px;
  height: ${t.sizes.ctaIcon}px;
  color: currentColor;
  transition: ${t.motion.arrow};
}

.service-category-sidebar-navigator-cta:hover .service-category-sidebar-navigator-cta-icon,
.service-category-sidebar-navigator-cta:focus-visible .service-category-sidebar-navigator-cta-icon,
.service-category-sidebar-navigator-support-link:hover .service-category-sidebar-navigator-support-icon,
.service-category-sidebar-navigator-support-link:focus-visible .service-category-sidebar-navigator-support-icon {
  transform: translateX(3px);
}

.service-category-sidebar-navigator-nav {
  min-width: 0;
}

.service-category-sidebar-navigator-list,
.service-category-sidebar-navigator-services {
  margin: 0;
  padding: 0;
  list-style: none;
}

.service-category-sidebar-navigator-list {
  display: grid;
  gap: ${t.spacing.categoryGap}px;
}

.service-category-sidebar-navigator-category {
  --scsn-item-accent: var(--scsn-accent);
  overflow: hidden;
  border: 1px solid var(--scsn-border);
  border-radius: ${t.radius.category}px;
  background: var(--scsn-surface);
  transition: ${t.motion.panel};
}

.service-category-sidebar-navigator-category:hover,
.service-category-sidebar-navigator-category[data-open="true"] {
  border-color: color-mix(in srgb, var(--scsn-item-accent) 28%, var(--scsn-border));
  box-shadow: var(--scsn-hover-shadow);
}

.service-category-sidebar-navigator-category-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  min-width: 0;
}

.service-category-sidebar-navigator-category-link,
.service-category-sidebar-navigator-category-toggle-link {
  display: grid;
  grid-template-columns: ${t.sizes.categoryIcon}px minmax(0, 1fr);
  align-items: center;
  min-width: 0;
  min-height: 62px;
  gap: 10px;
  padding: ${t.spacing.categoryPadding}px 8px ${t.spacing.categoryPadding}px ${t.spacing.categoryPadding}px;
  border: 0;
  color: var(--scsn-text);
  background: transparent;
  text-align: left;
  text-decoration: none;
}

.service-category-sidebar-navigator-category-link[data-has-icon="false"],
.service-category-sidebar-navigator-category-toggle-link[data-has-icon="false"] {
  grid-template-columns: minmax(0, 1fr);
}

.service-category-sidebar-navigator-category-toggle-link {
  width: 100%;
  cursor: pointer;
  font: inherit;
}

.service-category-sidebar-navigator-category-link:hover,
.service-category-sidebar-navigator-category-link:focus-visible,
.service-category-sidebar-navigator-category-toggle-link:hover,
.service-category-sidebar-navigator-category-toggle-link:focus-visible {
  color: var(--scsn-item-accent);
}

.service-category-sidebar-navigator-category-link:focus-visible,
.service-category-sidebar-navigator-category-toggle-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--scsn-item-accent) 18%, transparent);
  outline-offset: -3px;
}

.service-category-sidebar-navigator-category-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${t.sizes.categoryIcon}px;
  height: ${t.sizes.categoryIcon}px;
  border: 1px solid color-mix(in srgb, var(--scsn-item-accent) 16%, var(--scsn-border));
  border-radius: ${t.radius.icon}px;
  color: var(--scsn-item-accent);
  background: color-mix(in srgb, var(--scsn-item-accent) 9%, var(--scsn-icon-bg));
  transition: ${t.motion.icon};
}

.service-category-sidebar-navigator-category:hover .service-category-sidebar-navigator-category-icon-wrap,
.service-category-sidebar-navigator-category-link:hover .service-category-sidebar-navigator-category-icon-wrap,
.service-category-sidebar-navigator-category-link:focus-visible .service-category-sidebar-navigator-category-icon-wrap,
.service-category-sidebar-navigator-category-toggle-link:hover .service-category-sidebar-navigator-category-icon-wrap,
.service-category-sidebar-navigator-category-toggle-link:focus-visible .service-category-sidebar-navigator-category-icon-wrap {
  transform: translateY(-1px) scale(1.04);
  background: color-mix(in srgb, var(--scsn-item-accent) 14%, #ffffff);
}

.service-category-sidebar-navigator-category-icon {
  width: 18px;
  height: 18px;
  color: currentColor;
}

.service-category-sidebar-navigator-category-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.service-category-sidebar-navigator-category-title {
  min-width: 0;
  color: currentColor;
  font-size: ${t.typography.categoryTitle.fontSize}px;
  line-height: ${t.typography.categoryTitle.lineHeight};
  font-weight: ${t.typography.categoryTitle.fontWeight};
  letter-spacing: ${t.typography.categoryTitle.letterSpacing};
  overflow-wrap: anywhere;
}

.service-category-sidebar-navigator-category-description {
  min-width: 0;
  color: var(--scsn-text-muted);
  font-size: ${t.typography.categoryDescription.fontSize}px;
  line-height: ${t.typography.categoryDescription.lineHeight};
  font-weight: ${t.typography.categoryDescription.fontWeight};
  letter-spacing: ${t.typography.categoryDescription.letterSpacing};
  overflow-wrap: anywhere;
}

.service-category-sidebar-navigator-category-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: ${t.sizes.categoryAction}px;
  height: ${t.sizes.categoryAction}px;
  margin-right: 8px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  color: var(--scsn-item-accent);
  background: transparent;
  cursor: pointer;
  transition: ${t.motion.icon};
}

.service-category-sidebar-navigator-category-action:hover,
.service-category-sidebar-navigator-category-action:focus-visible {
  color: var(--scsn-accent-contrast);
  background: var(--scsn-item-accent);
  transform: scale(1.04);
}

.service-category-sidebar-navigator-category-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--scsn-item-accent) 18%, transparent);
  outline-offset: 2px;
}

.service-category-sidebar-navigator-category-action-icon {
  width: 17px;
  height: 17px;
  color: currentColor;
}

.service-category-sidebar-navigator-services-shell {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition: ${t.motion.list};
}

.service-category-sidebar-navigator-category[data-open="true"] .service-category-sidebar-navigator-services-shell {
  grid-template-rows: 1fr;
  opacity: 1;
}

.service-category-sidebar-navigator-services-clip {
  min-height: 0;
  overflow: hidden;
}

.service-category-sidebar-navigator-services {
  display: grid;
  gap: ${t.spacing.serviceGap}px;
  padding: 9px ${t.spacing.categoryPadding}px ${t.spacing.categoryPadding}px;
  border-top: 1px solid var(--scsn-border);
  background: color-mix(in srgb, var(--scsn-service-surface) 68%, transparent);
}

.service-category-sidebar-navigator-service-link {
  --scsn-service-accent: var(--scsn-item-accent);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
  min-height: 38px;
  gap: 9px;
  padding: ${t.spacing.servicePaddingY}px ${t.spacing.servicePaddingX}px;
  border-radius: ${t.radius.service}px;
  color: var(--scsn-text-soft);
  background: var(--scsn-service-surface);
  text-decoration: none;
  transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.service-category-sidebar-navigator-service-link:hover,
.service-category-sidebar-navigator-service-link:focus-visible {
  color: var(--scsn-service-accent);
  background: var(--scsn-service-surface-hover);
  transform: translateX(2px);
}

.service-category-sidebar-navigator-service-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--scsn-service-accent) 18%, transparent);
  outline-offset: 2px;
}

.service-category-sidebar-navigator-service-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.service-category-sidebar-navigator-service-main {
  display: inline-grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.service-category-sidebar-navigator-service-main[data-has-icon="false"] {
  grid-template-columns: minmax(0, 1fr);
}

.service-category-sidebar-navigator-service-icon {
  width: 15px;
  height: 15px;
  color: var(--scsn-service-accent);
}

.service-category-sidebar-navigator-service-title {
  min-width: 0;
  color: currentColor;
  font-size: ${t.typography.serviceTitle.fontSize}px;
  line-height: ${t.typography.serviceTitle.lineHeight};
  font-weight: ${t.typography.serviceTitle.fontWeight};
  letter-spacing: ${t.typography.serviceTitle.letterSpacing};
  overflow-wrap: anywhere;
}

.service-category-sidebar-navigator-service-description {
  color: var(--scsn-text-muted);
  font-size: ${t.typography.serviceDescription.fontSize}px;
  line-height: ${t.typography.serviceDescription.lineHeight};
  font-weight: ${t.typography.serviceDescription.fontWeight};
  letter-spacing: ${t.typography.serviceDescription.letterSpacing};
  overflow-wrap: anywhere;
}

.service-category-sidebar-navigator-service-arrow {
  width: ${t.sizes.serviceArrow}px;
  height: ${t.sizes.serviceArrow}px;
  color: var(--scsn-service-accent);
  transition: ${t.motion.arrow};
}

.service-category-sidebar-navigator-service-link:hover .service-category-sidebar-navigator-service-arrow,
.service-category-sidebar-navigator-service-link:focus-visible .service-category-sidebar-navigator-service-arrow {
  transform: translateX(3px);
}

.service-category-sidebar-navigator-empty {
  margin: 0;
  padding: 15px 12px;
  border: 1px solid var(--scsn-border);
  border-radius: ${t.radius.category}px;
  color: var(--scsn-text-muted);
  background: var(--scsn-service-surface);
  font-size: ${t.typography.empty.fontSize}px;
  line-height: ${t.typography.empty.lineHeight};
  font-weight: ${t.typography.empty.fontWeight};
  letter-spacing: ${t.typography.empty.letterSpacing};
}

.service-category-sidebar-navigator-support {
  margin-top: ${t.spacing.sectionGap}px;
  padding-top: ${t.spacing.sectionGap}px;
  border-top: 1px solid var(--scsn-strong-border);
}

.service-category-sidebar-navigator-support-text {
  margin: 0;
  color: var(--scsn-text-muted);
  font-size: ${t.typography.support.fontSize}px;
  line-height: ${t.typography.support.lineHeight};
  font-weight: ${t.typography.support.fontWeight};
  letter-spacing: ${t.typography.support.letterSpacing};
}

@media (max-width: 420px) {
  .service-category-sidebar-navigator-panel {
    padding: 14px;
  }

  .service-category-sidebar-navigator-title {
    font-size: 20px;
  }

  .service-category-sidebar-navigator-category-link,
  .service-category-sidebar-navigator-category-toggle-link {
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 9px;
    padding-left: 10px;
  }

  .service-category-sidebar-navigator-category-link[data-has-icon="false"],
  .service-category-sidebar-navigator-category-toggle-link[data-has-icon="false"] {
    grid-template-columns: minmax(0, 1fr);
  }

  .service-category-sidebar-navigator-category-icon-wrap {
    width: 34px;
    height: 34px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function toKey(id: NavigatorId): string {
  return String(id);
}

function normalise(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function stringify(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(stringify).filter(Boolean).join(" ");
  return "";
}

function getEntityTitle(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService,
  fallback: string
): string {
  return entity.title || entity.name || entity.label || fallback;
}

function getEntityDescription(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService
): string | undefined {
  return entity.text || entity.description;
}

function getEntityAccent(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService,
  fallback: string
): string {
  return entity.accentColor || entity.accentColour || entity.accentDarkColor || fallback;
}

function getEntityHref(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService,
  fallbackCategoryType = "services"
): string | undefined {
  const ctaHref = entity.cta?.href || entity.ctaHref;
  if (ctaHref) return ctaHref;
  if (entity.href || entity.link) return entity.href || entity.link;

  const categoryType =
    "categoryType" in entity && typeof entity.categoryType === "string"
      ? entity.categoryType
      : entity.headCategory?.categoryType;

  if (entity.slug) {
    return `/${categoryType || fallbackCategoryType}/${entity.slug}`;
  }

  return undefined;
}

function getEntityTarget(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService
): string | undefined {
  return entity.cta?.target || entity.target;
}

function getEntityRel(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService
): string | undefined {
  return entity.cta?.rel || entity.rel;
}

function getEntityAriaLabel(
  entity: ServiceCategorySidebarNavigatorCategory | ServiceCategorySidebarNavigatorService,
  fallback: string
): string | undefined {
  return entity.cta?.ariaLabel || entity.ctaAriaLabel || entity.ariaLabel || fallback;
}

function getCategoryChildren(
  category: ServiceCategorySidebarNavigatorCategory
): ServiceCategorySidebarNavigatorService[] {
  return category.services || category.items || category.serviceCategories || [];
}

function getSectionText(
  service: CategoryDetails | undefined,
  description: React.ReactNode,
  text: React.ReactNode
) {
  if (text) return text;
  if (description) return description;
  if (Array.isArray(service?.description)) return service.description.join(" ");
  return undefined;
}

function getCta({
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
}: {
  cta?: ServiceCategorySidebarNavigatorCta;
  ctaLabel?: React.ReactNode;
  ctaHref?: string;
  ctaAriaLabel?: string;
}): ServiceCategorySidebarNavigatorCta | undefined {
  const label = cta?.label || ctaLabel;
  const href = cta?.href || ctaHref;

  if (!label || !href) return undefined;

  return {
    label,
    href,
    ariaLabel: cta?.ariaLabel || ctaAriaLabel,
    target: cta?.target,
    rel: cta?.rel,
    icon: cta?.icon,
    iconToken: cta?.iconToken,
  };
}

function getOpenKeySet(
  categories: ResolvedCategory[],
  openValue?: boolean | NavigatorId[]
): Set<string> {
  if (openValue === true) return new Set(categories.map((category) => category.key));
  if (Array.isArray(openValue)) return new Set(openValue.map(toKey));
  return new Set();
}

function getOpenIdsFromKeys(categories: ResolvedCategory[], keys: Set<string>): NavigatorId[] {
  return categories.filter((category) => keys.has(category.key)).map((category) => category.id);
}

function resolveCategories({
  categories,
  groups,
  services,
  items,
  sourceService,
  navTitle,
  accentColor,
}: {
  categories?: ServiceCategorySidebarNavigatorCategory[];
  groups?: ServiceCategorySidebarNavigatorCategory[];
  services?: ServiceCategorySidebarNavigatorService[];
  items?: ServiceCategorySidebarNavigatorService[];
  sourceService?: CategoryDetails;
  navTitle?: string;
  accentColor: string;
}): ResolvedCategory[] {
  const explicitCategories = groups?.length ? groups : categories;
  const directServices = services?.length ? services : items;
  let sourceCategories: ServiceCategorySidebarNavigatorCategory[] = [];

  if (explicitCategories?.length) {
    sourceCategories = explicitCategories;
  } else if (sourceService?.subCategories?.length) {
    const modelCategories = sourceService.subCategories as ServiceCategorySidebarNavigatorCategory[];
    const hasNestedServices = modelCategories.some((category) => getCategoryChildren(category).length);

    if (hasNestedServices) {
      sourceCategories = modelCategories;
    } else {
      sourceCategories = [
        {
          id: sourceService.id || sourceService.slug || "services",
          title: navTitle || sourceService.long_name || sourceService.name || sourceService.label || "Services",
          description: Array.isArray(sourceService.description)
            ? sourceService.description.join(" ")
            : undefined,
          services: sourceService.subCategories as ServiceCategorySidebarNavigatorService[],
          accentColor,
        },
      ];
    }
  } else if (directServices?.length) {
    sourceCategories = [
      {
        id: "services",
        title: navTitle || "Services",
        services: directServices,
        accentColor,
      },
    ];
  }

  return sourceCategories
    .filter((category) => category && category.id != null)
    .map((category) => {
      const categoryTitle = getEntityTitle(category, "Service category");
      const categoryDescription = getEntityDescription(category);
      const categoryAccent = getEntityAccent(category, accentColor);
      const categoryHref = getEntityHref(category);
      const servicesList = getCategoryChildren(category);
      const resolvedServices = servicesList
        .filter((service) => service && service.id != null)
        .map((service) => {
          const serviceTitle = getEntityTitle(service, "Service");
          const serviceDescription = getEntityDescription(service);
          const serviceAccent = getEntityAccent(service, categoryAccent);
          const serviceHref = getEntityHref(service) || "#";
          const searchText = normalise(
            [
              serviceTitle,
              serviceDescription,
              serviceHref,
              stringify(service.slug),
              stringify(service.tags),
            ].join(" ")
          );

          return {
            key: toKey(service.id),
            id: service.id,
            source: service,
            title: serviceTitle,
            description: serviceDescription,
            href: serviceHref,
            target: getEntityTarget(service),
            rel: getEntityRel(service),
            ariaLabel: getEntityAriaLabel(service, `View service: ${serviceTitle}`),
            accentColor: serviceAccent,
            icon: service.icon,
            iconToken: service.iconToken,
            arrowIcon: service.arrowIcon,
            arrowIconToken: service.arrowIconToken,
            searchText,
          } satisfies ResolvedService;
        });

      const categorySearchText = normalise(
        [
          categoryTitle,
          categoryDescription,
          categoryHref,
          stringify(category.slug),
          stringify(category.tags),
        ].join(" ")
      );

      return {
        key: toKey(category.id),
        id: category.id,
        source: category,
        title: categoryTitle,
        description: categoryDescription,
        href: categoryHref,
        target: getEntityTarget(category),
        rel: getEntityRel(category),
        ariaLabel: getEntityAriaLabel(category, `View service category: ${categoryTitle}`),
        accentColor: categoryAccent,
        icon: category.icon,
        iconToken: category.iconToken,
        arrowIcon: category.arrowIcon,
        arrowIconToken: category.arrowIconToken,
        expandIcon: category.expandIcon,
        expandIconToken: category.expandIconToken,
        collapseIcon: category.collapseIcon,
        collapseIconToken: category.collapseIconToken,
        services: resolvedServices,
        searchText: categorySearchText,
      } satisfies ResolvedCategory;
    });
}

function filterCategories(categories: ResolvedCategory[], query: string): ResolvedCategory[] {
  if (!query) return categories;

  return categories.reduce<ResolvedCategory[]>((matches, category) => {
    const categoryMatches = category.searchText.includes(query);
    const matchingServices = category.services.filter((service) => service.searchText.includes(query));

    if (categoryMatches) {
      matches.push(category);
      return matches;
    }

    if (matchingServices.length) {
      matches.push({
        ...category,
        services: matchingServices,
      });
    }

    return matches;
  }, []);
}

function TokenIcon({
  token,
  className,
}: {
  token: ServiceCategorySidebarNavigatorIconToken;
  className: string;
}) {
  const Icon = iconRegistry[token];
  return <Icon className={className} aria-hidden="true" focusable="false" />;
}

function IconSlot({
  icon,
  token,
  fallbackToken,
  className,
}: {
  icon?: React.ReactNode;
  token?: ServiceCategorySidebarNavigatorIconToken;
  fallbackToken: ServiceCategorySidebarNavigatorIconToken;
  className: string;
}) {
  if (icon) return <span className={className}>{icon}</span>;
  return <TokenIcon token={token || fallbackToken} className={className} />;
}

function CategoryLinkContent({
  category,
  showCategoryIcons,
  showCategoryDescriptions,
  categoryIconToken,
}: {
  category: ResolvedCategory;
  showCategoryIcons: boolean;
  showCategoryDescriptions: boolean;
  categoryIconToken: ServiceCategorySidebarNavigatorIconToken;
}) {
  return (
    <>
      {showCategoryIcons ? (
        <span className="service-category-sidebar-navigator-category-icon-wrap" aria-hidden="true">
          <IconSlot
            icon={category.icon}
            token={category.iconToken}
            fallbackToken={categoryIconToken}
            className="service-category-sidebar-navigator-category-icon"
          />
        </span>
      ) : null}
      <span className="service-category-sidebar-navigator-category-copy">
        <span className="service-category-sidebar-navigator-category-title">{category.title}</span>
        {showCategoryDescriptions && category.description ? (
          <span className="service-category-sidebar-navigator-category-description">
            {category.description}
          </span>
        ) : null}
      </span>
    </>
  );
}

export default function ServiceCategorySidebarNavigator({
  service,
  serviceCategory,
  categories,
  groups,
  services,
  items,
  Link = DefaultLink,
  title,
  titleComponent = "h2",
  description,
  text,
  navTitle = "Service category navigation",
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
  supportText,
  supportLink,
  supportLinkLabel,
  supportLinkHref,
  supportLinkAriaLabel,
  supportTextSuffix,
  itemCtaLabel = "View service",
  categoryCtaLabel = "View category",
  searchPlaceholder = "Search services...",
  searchAriaLabel = "Search services",
  searchValue,
  defaultSearchValue = "",
  onSearchChange,
  showSearch = true,
  showCategoryIcons = true,
  showServiceIcons = false,
  showCategoryDescriptions = false,
  showServiceDescriptions = false,
  defaultOpen = false,
  initiallyOpen,
  defaultOpenCategories,
  openCategories,
  allowMultipleOpen = true,
  onOpenCategoriesChange,
  onCategoryClick,
  onServiceClick,
  categoryIconToken = t.icons.category,
  serviceIconToken = "wrench",
  categoryArrowIconToken = t.icons.categoryArrow,
  serviceArrowIconToken = t.icons.serviceArrow,
  expandIconToken = t.icons.expand,
  collapseIconToken = t.icons.collapse,
  colors,
  colours,
  accentColor,
  accentColour,
  backgroundColor,
  surfaceColor,
  serviceSurfaceColor,
  textColor,
  borderColor,
  maxWidth = t.sizes.maxWidth,
  className = "",
  style,
  ariaLabel,
  emptyText = "No services found.",
}: ServiceCategorySidebarNavigatorProps) {
  const sourceService = serviceCategory || service;
  const mergedColors = { ...colors, ...colours };
  const resolvedAccent = accentColour || accentColor || mergedColors.accent || t.colors.accent;
  const resolvedTitle =
    title || sourceService?.long_name || sourceService?.name || sourceService?.label || "Our Services";
  const resolvedDescription = getSectionText(sourceService, description, text);
  const resolvedCta = getCta({ cta, ctaLabel, ctaHref, ctaAriaLabel });
  const resolvedSupportLink = getCta({
    cta: supportLink,
    ctaLabel: supportLinkLabel,
    ctaHref: supportLinkHref,
    ctaAriaLabel: supportLinkAriaLabel,
  });

  const resolvedCategories = useMemo(
    () =>
      resolveCategories({
        categories,
        groups,
        services,
        items,
        sourceService,
        navTitle,
        accentColor: resolvedAccent,
      }),
    [categories, groups, services, items, sourceService, navTitle, resolvedAccent]
  );

  const initialOpenValue =
    defaultOpenCategories === undefined ? initiallyOpen ?? defaultOpen : defaultOpenCategories;
  const [localOpenKeys, setLocalOpenKeys] = useState<Set<string>>(() =>
    getOpenKeySet(resolvedCategories, initialOpenValue)
  );
  const [localSearch, setLocalSearch] = useState(defaultSearchValue);
  const query = searchValue === undefined ? localSearch : searchValue;
  const normalisedQuery = normalise(query);
  const controlledOpenKeys = openCategories === undefined ? undefined : getOpenKeySet(resolvedCategories, openCategories);
  const openKeys = controlledOpenKeys || localOpenKeys;
  const visibleCategories = useMemo(
    () => filterCategories(resolvedCategories, normalisedQuery),
    [resolvedCategories, normalisedQuery]
  );
  const HeadingTag = titleComponent;

  if (!resolvedCategories.length) return null;

  const handleSearchChange = (nextValue: string) => {
    if (searchValue === undefined) setLocalSearch(nextValue);
    onSearchChange?.(nextValue);
  };

  const updateOpenKeys = (nextKeys: Set<string>) => {
    if (!controlledOpenKeys) setLocalOpenKeys(nextKeys);
    onOpenCategoriesChange?.(getOpenIdsFromKeys(resolvedCategories, nextKeys));
  };

  const toggleCategory = (category: ResolvedCategory) => {
    const nextKeys = new Set(openKeys);
    if (nextKeys.has(category.key)) {
      nextKeys.delete(category.key);
    } else {
      if (!allowMultipleOpen) nextKeys.clear();
      nextKeys.add(category.key);
    }
    updateOpenKeys(nextKeys);
  };

  return (
    <aside
      className={`service-category-sidebar-navigator ${className}`.trim()}
      aria-label={ariaLabel || (typeof resolvedTitle === "string" ? resolvedTitle : navTitle)}
      style={
        {
          "--scsn-bg": backgroundColor || mergedColors.background,
          "--scsn-surface": surfaceColor || mergedColors.surface,
          "--scsn-service-surface": serviceSurfaceColor || mergedColors.serviceSurface,
          "--scsn-text": textColor || mergedColors.text,
          "--scsn-border": borderColor || mergedColors.border,
          "--scsn-accent": resolvedAccent,
          "--scsn-max-width": getCssSize(maxWidth),
          ...style,
        } as CssVarStyle
      }
    >
      <style>{serviceCategorySidebarNavigatorCss}</style>

      <div className="service-category-sidebar-navigator-panel">
        {showSearch ? (
          <div className="service-category-sidebar-navigator-search">
            <TokenIcon token={t.icons.search} className="service-category-sidebar-navigator-search-icon" />
            <input
              className="service-category-sidebar-navigator-input"
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
            {query ? (
              <button
                type="button"
                className="service-category-sidebar-navigator-clear"
                aria-label="Clear service search"
                onClick={() => handleSearchChange("")}
              >
                <TokenIcon token={t.icons.clear} className="service-category-sidebar-navigator-clear-icon" />
              </button>
            ) : null}
          </div>
        ) : null}

        <header className="service-category-sidebar-navigator-header">
          {resolvedTitle ? (
            <HeadingTag className="service-category-sidebar-navigator-title">{resolvedTitle}</HeadingTag>
          ) : null}
          {resolvedDescription ? (
            <p className="service-category-sidebar-navigator-description">{resolvedDescription}</p>
          ) : null}
          {resolvedCta ? (
            <Link
              href={resolvedCta.href}
              className="service-category-sidebar-navigator-cta"
              aria-label={resolvedCta.ariaLabel || (typeof resolvedCta.label === "string" ? resolvedCta.label : undefined)}
              target={resolvedCta.target}
              rel={resolvedCta.rel}
            >
              <span>{resolvedCta.label}</span>
              <IconSlot
                icon={resolvedCta.icon}
                token={resolvedCta.iconToken}
                fallbackToken={t.icons.cta}
                className="service-category-sidebar-navigator-cta-icon"
              />
            </Link>
          ) : null}
        </header>

        <nav className="service-category-sidebar-navigator-nav" aria-label={navTitle}>
          {visibleCategories.length ? (
            <ul className="service-category-sidebar-navigator-list">
              {visibleCategories.map((category) => {
                const hasServices = category.services.length > 0;
                const isOpen = Boolean(normalisedQuery) || openKeys.has(category.key);
                const categoryStyle = {
                  "--scsn-item-accent": category.accentColor,
                } as CssVarStyle;

                return (
                  <li
                    key={category.key}
                    className="service-category-sidebar-navigator-category"
                    data-open={isOpen}
                    style={categoryStyle}
                  >
                    <div className="service-category-sidebar-navigator-category-row">
                      {category.href ? (
                        <Link
                          href={category.href}
                          className="service-category-sidebar-navigator-category-link"
                          data-has-icon={showCategoryIcons}
                          title={category.title}
                          aria-label={category.ariaLabel || `${categoryCtaLabel}: ${category.title}`}
                          target={category.target}
                          rel={category.rel}
                          onClick={(event) => onCategoryClick?.(category.source, event)}
                        >
                          <CategoryLinkContent
                            category={category}
                            showCategoryIcons={showCategoryIcons}
                            showCategoryDescriptions={showCategoryDescriptions}
                            categoryIconToken={categoryIconToken}
                          />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="service-category-sidebar-navigator-category-toggle-link"
                          data-has-icon={showCategoryIcons}
                          aria-expanded={isOpen}
                          onClick={(event) => {
                            onCategoryClick?.(category.source, event);
                            toggleCategory(category);
                          }}
                        >
                          <CategoryLinkContent
                            category={category}
                            showCategoryIcons={showCategoryIcons}
                            showCategoryDescriptions={showCategoryDescriptions}
                            categoryIconToken={categoryIconToken}
                          />
                        </button>
                      )}

                      {hasServices ? (
                        <button
                          type="button"
                          className="service-category-sidebar-navigator-category-action"
                          aria-label={`${isOpen ? "Collapse" : "Expand"} ${category.title}`}
                          aria-expanded={isOpen}
                          onClick={() => toggleCategory(category)}
                        >
                          <IconSlot
                            icon={isOpen ? category.collapseIcon : category.expandIcon}
                            token={
                              isOpen
                                ? category.collapseIconToken || collapseIconToken
                                : category.expandIconToken || expandIconToken
                            }
                            fallbackToken={isOpen ? collapseIconToken : expandIconToken}
                            className="service-category-sidebar-navigator-category-action-icon"
                          />
                        </button>
                      ) : category.href ? (
                        <span className="service-category-sidebar-navigator-category-action" aria-hidden="true">
                          <IconSlot
                            icon={category.arrowIcon}
                            token={category.arrowIconToken}
                            fallbackToken={categoryArrowIconToken}
                            className="service-category-sidebar-navigator-category-action-icon"
                          />
                        </span>
                      ) : null}
                    </div>

                    {hasServices && isOpen ? (
                      <div className="service-category-sidebar-navigator-services-shell">
                        <div className="service-category-sidebar-navigator-services-clip">
                          <ul className="service-category-sidebar-navigator-services">
                            {category.services.map((serviceItem) => {
                              const hasServiceIcon = Boolean(showServiceIcons && (serviceItem.icon || serviceItem.iconToken || serviceIconToken));

                              return (
                                <li key={serviceItem.key}>
                                  <Link
                                    href={serviceItem.href}
                                    className="service-category-sidebar-navigator-service-link"
                                    title={serviceItem.title}
                                    aria-label={serviceItem.ariaLabel || `${itemCtaLabel}: ${serviceItem.title}`}
                                    target={serviceItem.target}
                                    rel={serviceItem.rel}
                                    style={
                                      { "--scsn-service-accent": serviceItem.accentColor } as CssVarStyle
                                    }
                                    onClick={(event) =>
                                      onServiceClick?.(serviceItem.source, category.source, event)
                                    }
                                  >
                                    <span className="service-category-sidebar-navigator-service-copy">
                                      <span
                                        className="service-category-sidebar-navigator-service-main"
                                        data-has-icon={hasServiceIcon}
                                      >
                                        {hasServiceIcon ? (
                                          <IconSlot
                                            icon={serviceItem.icon}
                                            token={serviceItem.iconToken}
                                            fallbackToken={serviceIconToken}
                                            className="service-category-sidebar-navigator-service-icon"
                                          />
                                        ) : null}
                                        <span className="service-category-sidebar-navigator-service-title">
                                          {serviceItem.title}
                                        </span>
                                      </span>
                                      {showServiceDescriptions && serviceItem.description ? (
                                        <span className="service-category-sidebar-navigator-service-description">
                                          {serviceItem.description}
                                        </span>
                                      ) : null}
                                    </span>
                                    <IconSlot
                                      icon={serviceItem.arrowIcon}
                                      token={serviceItem.arrowIconToken}
                                      fallbackToken={serviceArrowIconToken}
                                      className="service-category-sidebar-navigator-service-arrow"
                                    />
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="service-category-sidebar-navigator-empty">{emptyText}</p>
          )}
        </nav>

        {supportText ? (
          <div className="service-category-sidebar-navigator-support">
            <p className="service-category-sidebar-navigator-support-text">
              {supportText}
              {resolvedSupportLink ? (
                <>
                  {" "}
                  <Link
                    href={resolvedSupportLink.href}
                    className="service-category-sidebar-navigator-support-link"
                    aria-label={
                      resolvedSupportLink.ariaLabel ||
                      (typeof resolvedSupportLink.label === "string"
                        ? resolvedSupportLink.label
                        : undefined)
                    }
                    target={resolvedSupportLink.target}
                    rel={resolvedSupportLink.rel}
                  >
                    <span>{resolvedSupportLink.label}</span>
                    <IconSlot
                      icon={resolvedSupportLink.icon}
                      token={resolvedSupportLink.iconToken}
                      fallbackToken={t.icons.cta}
                      className="service-category-sidebar-navigator-support-icon"
                    />
                  </Link>
                </>
              ) : null}
              {supportTextSuffix || ""}
            </p>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export {
  serviceCategorySidebarNavigatorIconTokens,
  serviceCategorySidebarNavigatorTokens,
  type ServiceCategorySidebarNavigatorColorToken,
  type ServiceCategorySidebarNavigatorIconToken,
} from "./ServiceCategorySidebarNavigator.tokens";
